// DivergingBar — a small inline horizontal diverging bar for table cells,
// shared by the ranked (bias), chain (call_net/put_net) and by_expiry (net_usd)
// renderers. A centred zero axis; positive fills right in flow.buy (blue),
// negative fills left in flow.sell (red), scaled to a caller-supplied max so a
// whole column/set shares one scale. Optionally prints the value at the outer
// end (chain uses this).
//
// Colours resolve to hex via getTokenValue (SVG presentation attributes don't
// read CSS variables), so bars recolour on theme change.

import { useThemeMode } from '../../../../providers/theme-provider';
import { getTokenValue } from '../../../../styles/tokens';
import { fmtUsd } from '../../../../providers/options-flow/options-flow.helpers';

interface DivergingBarProps {
  value: number;
  max: number;
  width?: number;
  height?: number;
  // Print fmtUsd(value) at the bar's outer end.
  showValue?: boolean;
  // Responsive: fill the container's width (svg width="100%") using percentage
  // coordinates, so the bar grows with a flexible (1fr) table column. Uses % x/
  // width — NOT a stretched viewBox — so the axis tick + corners stay crisp.
  fill?: boolean;
}

export const DivergingBar = ({
  value,
  max,
  width = 120,
  height = 14,
  showValue = false,
  fill = false,
}: DivergingBarProps) => {
  const { resolvedMode } = useThemeMode();
  const buy = getTokenValue('flow.buy', resolvedMode);
  const sell = getTokenValue('flow.sell', resolvedMode);
  const axis = getTokenValue('border.subtle', resolvedMode);
  const label = getTokenValue('text.secondary', resolvedMode);

  const safeMax = max > 0 ? max : 1;
  const positive = value >= 0;
  const barY = 3;
  const barH = height - 6;

  if (fill) {
    // Percentage geometry from a centred axis: each half is 50% of the width.
    const halfPct = (Math.min(1, Math.abs(value) / safeMax) * 100) / 2;
    return (
      <svg
        width="100%"
        height={height}
        style={{ verticalAlign: 'middle', display: 'block' }}
        role="presentation"
      >
        <line x1="50%" y1={1} x2="50%" y2={height - 1} stroke={axis} strokeWidth={1} />
        <rect
          x={positive ? '50%' : `${50 - halfPct}%`}
          y={barY}
          width={`${halfPct}%`}
          height={barH}
          rx={2}
          fill={positive ? buy : sell}
          opacity={0.9}
        />
      </svg>
    );
  }

  const mid = width / 2;
  const half = showValue ? mid - 2 : mid;
  const w = (Math.abs(value) / safeMax) * half;

  return (
    <svg width={width} height={height} style={{ verticalAlign: 'middle' }} role="presentation">
      <line x1={mid} y1={1} x2={mid} y2={height - 1} stroke={axis} strokeWidth={1} />
      <rect
        x={positive ? mid : mid - w}
        y={barY}
        width={w}
        height={barH}
        rx={2}
        fill={positive ? buy : sell}
        opacity={0.9}
      />
      {showValue && (
        <text
          x={positive ? width - 2 : 2}
          y={height - 3.5}
          fill={label}
          fontSize={10}
          textAnchor={positive ? 'end' : 'start'}
        >
          {fmtUsd(value)}
        </text>
      )}
    </svg>
  );
};
